// pages/Agents/AgentSpace/AgentSpaceLanding.js
// Main Agent Space landing page.
//
// Layout:
//   - Header: "Agents" title + subtitle + Grid/List view toggle (right)
//   - Top: agent cards / list rows (every available agent)
//   - Activity (cross-agent, info-panel style): tabs + search + filter +
//        chronological timeline of task cards with step bullets
//   - Scheduled tasks (cross-agent, info-panel style): scrollable table
//        with sticky header and pause/delete actions
//
// Activity + Scheduled mirror the visual treatment used inside the per-agent
// info drawer (AgentSpaceAgentInfoPanel) but include an agent badge on
// every row so the user can tell which agent each item belongs to.
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Skeleton,
    Tooltip,
    IconButton,
    Chip,
    Button,
    InputBase, // Added here
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HistoryIcon from '@mui/icons-material/History';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import axiosInstance from '../../../../utils/axiosInstance';
import useGeneralSourceConfigured from './useGeneralSourceConfigured';
import { useAgentSpace } from '../../../../contexts/AgentSpaceContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
};

const ICON_MAP = {
    PollIcon: (s) => <PollIcon sx={{ fontSize: s }} />,
    PeopleIcon: (s) => <PeopleIcon sx={{ fontSize: s }} />,
    ChatIcon: (s) => <ChatIcon sx={{ fontSize: s }} />,
    EventIcon: (s) => <EventIcon sx={{ fontSize: s }} />,
    MailOutlineIcon: (s) => <MailOutlineIcon sx={{ fontSize: s }} />,
    EmailIcon: (s) => <EmailIcon sx={{ fontSize: s }} />,
    CalendarMonthIcon: (s) => <CalendarMonthIcon sx={{ fontSize: s }} />,
    CleaningServicesIcon: (s) => <CleaningServicesIcon sx={{ fontSize: s }} />,
};

function getIcon(name, size = 20) {
    const fn = ICON_MAP[name];
    return fn ? fn(size) : <AutoAwesomeIcon sx={{ fontSize: size }} />;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    const now = new Date();
    const showYear = d.getFullYear() !== now.getFullYear();
    const datePart = d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(showYear ? { year: 'numeric' } : {}),
    });
    const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${datePart}, ${timePart}`;
}

function formatFuture(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return 'Due now';
    const min = Math.round(diff / 60000);
    const hr = Math.round(diff / 3600000);
    const day = Math.round(diff / 86400000);
    if (min < 60) return `in ${min}m`;
    if (hr < 24) return `in ${hr}h`;
    if (day < 14) return `in ${day}d`;
    return d.toLocaleDateString();
}

function shortNumber(n) {
    if (n === null || n === undefined) return '0';
    if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
    return String(n);
}

// Compact byte size — used by the attachment chips and the Files section.
function formatBytes(bytes) {
    if (bytes == null) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// Pick the most user-meaningful one-line label out of a result item — used
// by renderStepResults to render the raw output_data payload from a step.
function itemLabel(item) {
    if (item === null || item === undefined) return '';
    if (typeof item !== 'object') return String(item).slice(0, 120);
    for (const key of ['title', 'name', 'subject', 'label', 'email', 'session_title', 'invoice_number', 'event_name']) {
        const v = item[key];
        if (typeof v === 'string' && v.trim()) return v.slice(0, 120);
        if (typeof v === 'number') return String(v);
    }
    const entries = Object.entries(item).filter(([, v]) => v !== null && typeof v !== 'object');
    return entries.slice(0, 3).map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`).join(' · ');
}

function itemSubtitle(item) {
    if (typeof item !== 'object' || item === null) return null;
    const parts = [];
    for (const key of ['status', 'sentiment', 'created_at', 'updated_at', 'date', 'last_message_at']) {
        const v = item[key];
        if (typeof v === 'string' && v) parts.push(`${key.replace(/_/g, ' ')}: ${v.slice(0, 30)}`);
        else if (typeof v === 'number') parts.push(`${key.replace(/_/g, ' ')}: ${v}`);
    }
    return parts.slice(0, 3).join(' · ') || null;
}

// Compact preview of a step's truncated output payload — same look as the
// info panel so the experience is consistent across both surfaces.
function renderStepResults(data) {
    if (data === null || data === undefined) return null;

    let items = null;
    let total = null;
    let truncated = false;
    if (Array.isArray(data)) {
        items = data;
        total = data.length;
    } else if (typeof data === 'object') {
        if (Array.isArray(data.items)) {
            items = data.items;
            total = data._items_total != null ? data._items_total : (data.total != null ? data.total : items.length);
            truncated = data._items_truncated != null
                ? data._items_truncated
                : (data.truncated != null ? data.truncated : items.length < (total || 0));
        }
    }

    if (items) {
        if (items.length === 0) {
            return (
                <Typography sx={{ fontFamily, fontSize: '0.66rem', color: '#94a3b8' }}>
                    (no items)
                </Typography>
            );
        }
        return (
            <Box sx={{
                bgcolor: 'rgba(0,0,0,0.02)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '8px',
                px: 1, py: 0.6,
            }}>
                <Typography sx={{
                    fontFamily, fontSize: '0.58rem', fontWeight: 700, color: '#94a3b8',
                    textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.4,
                }}>
                    Results {total ? `· ${total} total` : ''}
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    {items.map((it, i) => {
                        const lbl = itemLabel(it);
                        const sub = itemSubtitle(it);
                        return (
                            <Box component="li" key={i} sx={{ fontFamily }}>
                                <Typography sx={{ fontFamily, fontSize: '0.68rem', color: '#1e293b', lineHeight: 1.4 }}>
                                    {lbl || '(unnamed item)'}
                                </Typography>
                                {sub && (
                                    <Typography sx={{ fontFamily, fontSize: '0.6rem', color: '#94a3b8' }}>
                                        {sub}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
                {truncated && total && (
                    <Typography sx={{
                        fontFamily, fontSize: '0.58rem', color: '#94a3b8', mt: 0.4, fontStyle: 'italic',
                    }}>
                        Showing the first {items.length} of {total} — ask the agent to filter for more.
                    </Typography>
                )}
            </Box>
        );
    }

    let text;
    try {
        text = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
    } catch (e) {
        text = String(data);
    }
    if (text.length > 800) text = text.slice(0, 800) + '…';
    return (
        <Box sx={{
            bgcolor: 'rgba(0,0,0,0.02)',
            border: '1px solid rgba(0,0,0,0.06)',
            borderRadius: '8px',
            px: 1, py: 0.6,
            maxHeight: 180, overflow: 'auto',
        }}>
            <Typography sx={{
                fontFamily: "'Fira Code', monospace",
                fontSize: '0.62rem', color: '#475569',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
                {text}
            </Typography>
        </Box>
    );
}

// Pagination knobs for the cross-agent sections.
const ACTIVITY_PAGE_SIZE = 6;
const SCHEDULED_PAGE_SIZE = 6;

// Small agent badge used inside the cross-agent activity + scheduled rows.
function AgentBadge({ agentType, agentMap }) {
    const def = agentMap[agentType];
    const color = def?.color || colors.accent;
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{
                width: 20, height: 20, borderRadius: '6px',
                bgcolor: `${color}14`, color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
            }}>
                {getIcon(def?.icon_name, 12)}
            </Box>
            <Typography sx={{ fontFamily, fontSize: '0.66rem', fontWeight: 600, color: colors.textSecondary }} noWrap>
                {def?.label || agentType}
            </Typography>
        </Box>
    );
}

export default function AgentSpaceLanding({ chatbotId, onSelectAgent, refreshKey }) {
    const { configureAgent, fabVisible, toggleFabVisible } = useAgentSpace();

    const [agentTypes, setAgentTypes] = useState([]);
    const [agentTypesLoading, setAgentTypesLoading] = useState(true);
    const [connectionStatuses, setConnectionStatuses] = useState({});
    const [agentMetrics, setAgentMetrics] = useState({});

    // The General agent has no OAuth connection — it's configured via its data
    // source instead, so drive its card status from that (otherwise it always
    // reads "Active"). refreshKey re-checks after a source is set/cleared.
    const generalConfigured = useGeneralSourceConfigured(chatbotId, { refreshKey });

    // 'cards' (default, screenshot-style tiles) or 'list' (compact rows).
    const [viewMode, setViewMode] = useState('cards');

    // ---- Load agent types + connection statuses -----------------------
    useEffect(() => {
        if (!chatbotId) return;
        let cancelled = false;
        setAgentTypesLoading(true);
        Promise.all([
            axiosInstance.get('/api/agent-space/agent-types'),
            axiosInstance.get(`/api/agent-space/connections?chatbot_id=${chatbotId}`),
        ])
            .then(([typesRes, connRes]) => {
                if (cancelled) return;
                setAgentTypes(typesRes.data?.data || []);
                setConnectionStatuses(connRes.data?.data || {});
            })
            .catch(() => { /* leave defaults */ })
            .finally(() => { if (!cancelled) setAgentTypesLoading(false); });
        return () => { cancelled = true; };
    }, [chatbotId, refreshKey]);

    // Pull per-agent task summaries for the card metric.
    useEffect(() => {
        if (!chatbotId || agentTypes.length === 0) return;
        let cancelled = false;
        Promise.all(agentTypes.map((a) =>
            axiosInstance
                .get('/api/agent-space/dashboard/task-summaries', {
                    params: { chatbot_id: chatbotId, agent_type: a.id, page: 1, per_page: 1 },
                })
                .then((res) => ({ id: a.id, data: res.data?.data || {} }))
                .catch(() => ({ id: a.id, data: {} }))
        )).then((rows) => {
            if (cancelled) return;
            const map = {};
            rows.forEach((row) => { map[row.id] = row.data.agent_metrics || {}; });
            setAgentMetrics(map);
        });
        return () => { cancelled = true; };
    }, [chatbotId, agentTypes, refreshKey]);

    const agentMap = useMemo(() => {
        const map = {};
        for (const a of agentTypes) map[a.id] = a;
        return map;
    }, [agentTypes]);

    // ============================================================
    // CROSS-AGENT ACTIVITY (info-panel style)
    // ============================================================
    const [activity, setActivity] = useState([]);
    const [activityLoading, setActivityLoading] = useState(true);
    const [activityPage, setActivityPage] = useState(1);
    const [activityHasMore, setActivityHasMore] = useState(false);
    const [activityLoadingMore, setActivityLoadingMore] = useState(false);
    const activityScrollRef = useRef(null);
    const prevActivityScrollRef = useRef(null);

    const [statusFilter, setStatusFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [expandedTasks, setExpandedTasks] = useState(() => new Set());
    const toggleExpand = (taskId) => {
        setExpandedTasks((prev) => {
            const next = new Set(prev);
            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            return next;
        });
    };

    // Three call modes:
    //   - append=true  → load OLDER page, push to end of the array
    //   - silent=true  → background poll fetching page 1. MUST NOT wipe older
    //     pages the user has already scrolled/clicked to load. We merge new
    //     items in: update items that came back in the response (preserving
    //     object identity when nothing changed), prepend genuinely-new items
    //     (newer than anything we have), keep everything else as-is.
    //   - else (silent=false, append=false) → explicit refresh. Replace.
    const fetchActivity = useCallback((page = 1, append = false, silent = false) => {
        if (!chatbotId) return;
        if (!silent) {
            if (!append) setActivityLoading(true);
            else setActivityLoadingMore(true);
        }
        // agent_type=all so we collect activity from every agent at once.
        axiosInstance.get('/api/agent-space/dashboard/activities', {
            params: { chatbot_id: chatbotId, agent_type: 'all', page, per_page: ACTIVITY_PAGE_SIZE },
        })
            .then((res) => {
                if (!res.data?.success) return;
                const items = res.data.data?.activities || [];
                setActivity((prev) => {
                    if (append) return [...prev, ...items];
                    if (!prev || prev.length === 0) return items;

                    // Helper: do these two session payloads have the same
                    // shape (step/turn/file counts, status, timestamp)? If
                    // yes we keep the old object for referential equality.
                    const sameShape = (a, b) => (
                        (a.metadata?.step_count || 0) === (b.metadata?.step_count || 0)
                        && (a.metadata?.turn_count || 0) === (b.metadata?.turn_count || 0)
                        && (a.metadata?.file_count || 0) === (b.metadata?.file_count || 0)
                        && a.status === b.status
                        && a.timestamp === b.timestamp
                    );

                    if (silent) {
                        // MERGE mode — never drop already-loaded older pages.
                        const newById = new Map(items.map((n) => [n.id, n]));
                        const seenIds = new Set();
                        const merged = prev.map((p) => {
                            const next = newById.get(p.id);
                            if (!next) return p;          // not in this fetch — keep as-is
                            seenIds.add(p.id);
                            return sameShape(p, next) ? p : next;
                        });
                        const brandNew = items.filter((n) => !seenIds.has(n.id));
                        // The API returns newest first → prepend new items.
                        return brandNew.length > 0 ? [...brandNew, ...merged] : merged;
                    }

                    // Non-silent explicit refresh — replace, but reuse old
                    // object refs where shape hasn't changed to avoid churn.
                    const prevById = new Map(prev.map((p) => [p.id, p]));
                    return items.map((next) => {
                        const old = prevById.get(next.id);
                        return old && sameShape(old, next) ? old : next;
                    });
                });
                // Don't shrink hasMore during silent polls — page 1 always
                // reports has_more=true even after we've loaded page 3+, and
                // we'd never want to FLIP it to false based on page 1's view.
                if (!silent) {
                    setActivityHasMore(!!res.data.data?.has_more);
                    setActivityPage(res.data.data?.page || page);
                } else if (append) {
                    // append shouldn't be combined with silent, but just in case.
                    setActivityHasMore(!!res.data.data?.has_more);
                }
            })
            .catch(() => { if (!append && !silent) setActivity([]); })
            .finally(() => {
                if (!silent) {
                    setActivityLoading(false);
                    setActivityLoadingMore(false);
                }
            });
    }, [chatbotId]);

    const handleActivityScroll = useCallback((e) => {
        if (activityLoadingMore || activityLoading || !activityHasMore) return;
        if (e.target.scrollTop <= 32) {
            prevActivityScrollRef.current = {
                height: e.target.scrollHeight,
                top: e.target.scrollTop,
            };
            fetchActivity(activityPage + 1, true);
        }
    }, [activityLoadingMore, activityLoading, activityHasMore, activityPage, fetchActivity]);

    // Manual "Load previous activity" click — same effect as scrolling to the
    // top of the list. Captures the current scroll position so the user
    // doesn't lose their place when older items prepend.
    const handleLoadOlderActivity = useCallback(() => {
        if (activityLoadingMore || activityLoading || !activityHasMore) return;
        const container = activityScrollRef.current;
        if (container) {
            prevActivityScrollRef.current = {
                height: container.scrollHeight,
                top: container.scrollTop,
            };
        }
        fetchActivity(activityPage + 1, true);
    }, [activityLoadingMore, activityLoading, activityHasMore, activityPage, fetchActivity]);

    useEffect(() => {
        setActivityPage(1);
        setActivityHasMore(false);
        fetchActivity(1, false);
    }, [fetchActivity, refreshKey]);

    // Background polling so scheduled-task runs appear in real time on the
    // cross-agent activity feed without needing a manual refresh. Uses the
    // silent path (no loading-skeleton flash) and diff-merges so unchanged
    // cards keep their DOM nodes — no blink, no reflow.
    useEffect(() => {
        if (!chatbotId) return;
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') {
                fetchActivity(1, false, true);
            }
        }, 10000);
        return () => clearInterval(intervalId);
    }, [chatbotId, fetchActivity]);

    // Smart scroll handling. Same three cases as the info panel:
    //   1. Older-page prepend → restore the user's previous scroll position
    //   2. Very first load → scroll to bottom (latest task)
    //   3. Silent poll added/removed a session → only follow if the user was
    //      already pinned at the bottom (within ~80px). Reading higher up?
    //      Leave them there.
    const lastActivityLenRef = useRef(0);
    const initialLoadDoneRef = useRef(false);
    useEffect(() => {
        const container = activityScrollRef.current;
        if (!container) return;
        if (prevActivityScrollRef.current) {
            const { height: prevHeight, top: prevTop } = prevActivityScrollRef.current;
            const diff = container.scrollHeight - prevHeight;
            if (diff > 0) container.scrollTop = prevTop + diff;
            prevActivityScrollRef.current = null;
        } else if (!initialLoadDoneRef.current && activityPage === 1 && activity.length > 0) {
            container.scrollTop = container.scrollHeight;
            initialLoadDoneRef.current = true;
        } else if (activity.length !== lastActivityLenRef.current) {
            const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
            if (distanceFromBottom < 80) {
                container.scrollTop = container.scrollHeight;
            }
        }
        lastActivityLenRef.current = activity.length;
    }, [activity, activityPage]);

    // Build per-task entries (each backend activity = one full session
    // with all turns: user prompts + agent replies + step details).
    const tasksChronological = useMemo(() => {
        return [...activity].reverse().map((act) => ({
            id: act.id,
            session_code: act.metadata?.session_code,
            title: act.resource_name || 'Untitled task',
            description: act.description,
            status: act.status,
            timestamp: act.timestamp,
            // Flat step list (kept for the "+ N more" expander).
            steps: act.step_details || [],
            stepCount: act.metadata?.step_count || (act.step_details || []).length,
            // Per-turn conversation (user → agent → user → agent …).
            turns: act.turns || [],
            turnCount: act.metadata?.turn_count || (act.turns || []).length,
            // Files generated during this session (large tool output spilled
            // to GCS). Rendered as attachment chips at the bottom of the card.
            files: act.files || [],
            fileCount: act.metadata?.file_count || (act.files || []).length,
            agent_type: act.agent_type,
        }));
    }, [activity]);

    const activityCounts = useMemo(() => ({
        all: tasksChronological.length,
        completed: tasksChronological.filter((t) => t.status === 'completed').length,
        failed: tasksChronological.filter((t) => t.status === 'failed').length,
    }), [tasksChronological]);

    const filteredActivity = useMemo(() => {
        let rows = tasksChronological;
        if (statusFilter === 'completed') rows = rows.filter((t) => t.status === 'completed');
        else if (statusFilter === 'failed') rows = rows.filter((t) => t.status === 'failed');
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter((t) =>
                (t.title || '').toLowerCase().includes(q)
                || (t.description || '').toLowerCase().includes(q)
                || (agentMap[t.agent_type]?.label || '').toLowerCase().includes(q)
                || (t.steps || []).some((s) =>
                    (s.label || '').toLowerCase().includes(q)
                    || (s.output_summary || '').toLowerCase().includes(q)
                )
                || (t.turns || []).some((tr) =>
                    (tr.message || '').toLowerCase().includes(q)
                    || (tr.step_details || []).some((s) =>
                        (s.label || '').toLowerCase().includes(q)
                        || (s.output_summary || '').toLowerCase().includes(q)
                    )
                )
            );
        }
        return rows;
    }, [tasksChronological, statusFilter, search, agentMap]);

    // ============================================================
    // CROSS-AGENT FILES (agent-generated, sourced from agent_space_files)
    // ============================================================
    // Files spilled to GCS by the engine when tool output is too large to
    // inline (Excel dumps, big reports, etc.) or written explicitly by an
    // action. Listed newest-first, separate from the activity feed so the
    // user can grab a download without scrolling through conversations.
    const [agentFiles, setAgentFiles] = useState([]);
    const [agentFilesLoading, setAgentFilesLoading] = useState(true);
    // Files-section filter UI (mirrors the activity-feed pattern).
    // Pills: All | Excel | CSV | JSON | PDF | Text | Other (only the ones
    // that actually have files in the response are shown).
    const [filesSearch, setFilesSearch] = useState('');
    const [filesFilter, setFilesFilter] = useState('all');

    // `silent=true` is for the background poll — skips the loading skeleton
    // toggle and merges the response into the existing array instead of
    // wholesale replacing. Items that haven't changed keep their object
    // identity so React doesn't re-render their cards.
    const fetchAgentFiles = useCallback((silent = false) => {
        if (!chatbotId) return;
        if (!silent) setAgentFilesLoading(true);
        axiosInstance.get('/api/agent-space/files', {
            params: { chatbot_id: chatbotId, limit: 50 },
        })
            .then((res) => {
                if (!res.data?.success) return;
                const items = res.data.data?.files || [];
                setAgentFiles((prev) => {
                    if (!silent || !prev || prev.length === 0) return items;
                    // MERGE: keep prev order, swap in updated rows by id
                    // (with referential equality when nothing changed), and
                    // prepend genuinely-new files at the top.
                    const sameShape = (a, b) => (
                        a.id === b.id
                        && a.file_size === b.file_size
                        && a.total_chars === b.total_chars
                        && a.filename === b.filename
                    );
                    const newById = new Map(items.map((n) => [n.id, n]));
                    const seenIds = new Set();
                    const merged = prev.map((p) => {
                        const next = newById.get(p.id);
                        if (!next) return p;
                        seenIds.add(p.id);
                        return sameShape(p, next) ? p : next;
                    });
                    const brandNew = items.filter((n) => !seenIds.has(n.id));
                    return brandNew.length > 0 ? [...brandNew, ...merged] : merged;
                });
            })
            .catch(() => { if (!silent) setAgentFiles([]); })
            .finally(() => { if (!silent) setAgentFilesLoading(false); });
    }, [chatbotId]);

    useEffect(() => {
        fetchAgentFiles();
    }, [fetchAgentFiles, refreshKey]);

    // Poll every 10s when the tab is visible. Uses silent + merge so the
    // file list doesn't flicker or lose scroll between refreshes.
    useEffect(() => {
        if (!chatbotId) return;
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') fetchAgentFiles(true);
        }, 10000);
        return () => clearInterval(intervalId);
    }, [chatbotId, fetchAgentFiles]);

    // ============================================================
    // CROSS-AGENT SCHEDULED TASKS (info-panel style)
    // ============================================================
    const [scheduled, setScheduled] = useState([]);
    const [scheduledLoading, setScheduledLoading] = useState(true);
    // Scheduled-section filter UI (same pattern as activity feed).
    // Pills: All | Active | Paused (rendered only when both populations exist).
    const [scheduledSearch, setScheduledSearch] = useState('');
    const [scheduledFilter, setScheduledFilter] = useState('all');
    const [scheduledVisible, setScheduledVisible] = useState(SCHEDULED_PAGE_SIZE);
    const scheduledScrollRef = useRef(null);

    const fetchScheduled = useCallback((silent = false) => {
        if (!chatbotId) return;
        if (!silent) setScheduledLoading(true);
        // No agent_type → all scheduled tasks across all agents.
        axiosInstance.get('/api/agent-space/scheduled-tasks', {
            params: { chatbot_id: chatbotId },
        })
            .then((res) => {
                if (!res.data?.success) return;
                const items = res.data.data || [];
                setScheduled((prev) => {
                    if (!silent || !prev || prev.length === 0) return items;
                    // MERGE: scheduled-task list is small, but we still
                    // preserve identity for unchanged rows (so pause/resume
                    // animations don't get reset by polling) and prepend
                    // newly-created tasks at the top.
                    const sameShape = (a, b) => (
                        a.id === b.id
                        && a.status === b.status
                        && a.frequency === b.frequency
                        && a.interval_value === b.interval_value
                        && a.next_run_at === b.next_run_at
                        && a.last_run_at === b.last_run_at
                    );
                    const newById = new Map(items.map((n) => [n.id, n]));
                    const seenIds = new Set();
                    const merged = prev
                        .map((p) => {
                            const next = newById.get(p.id);
                            if (!next) return null;  // task was deleted server-side — drop
                            seenIds.add(p.id);
                            return sameShape(p, next) ? p : next;
                        })
                        .filter(Boolean);
                    const brandNew = items.filter((n) => !seenIds.has(n.id));
                    return brandNew.length > 0 ? [...brandNew, ...merged] : merged;
                });
            })
            .catch(() => { if (!silent) setScheduled([]); })
            .finally(() => { if (!silent) setScheduledLoading(false); });
    }, [chatbotId]);

    useEffect(() => {
        setScheduledVisible(SCHEDULED_PAGE_SIZE);
        fetchScheduled();
    }, [fetchScheduled, refreshKey]);

    // Poll scheduled tasks every 10s too, so freshly-created schedules and
    // post-run next_run_at updates show up without manual refresh. Same
    // silent+merge contract as files and activity above.
    useEffect(() => {
        if (!chatbotId) return;
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') fetchScheduled(true);
        }, 10000);
        return () => clearInterval(intervalId);
    }, [chatbotId, fetchScheduled]);

    const handleScheduledScroll = useCallback((e) => {
        // Scroll-up grows the visible window. Use the FILTERED list as the
        // ceiling so a tight filter doesn't try to reveal more rows than the
        // filter is showing.
        const total = scheduled.length;  // grow up to the unfiltered total
        if (e.target.scrollTop <= 32 && scheduledVisible < total) {
            setScheduledVisible((v) => Math.min(v + SCHEDULED_PAGE_SIZE, total));
        }
    }, [scheduled.length, scheduledVisible]);

    const toggleScheduled = async (task) => {
        const next = task.status === 'active' ? 'paused' : 'active';
        try {
            const resp = await axiosInstance.patch(`/api/agent-space/scheduled-tasks/${task.id}`, { status: next });
            if (resp.data?.success) {
                setScheduled((prev) => prev.map((t) => (t.id === task.id ? resp.data.data : t)));
            }
        } catch (e) { console.error(e); }
    };

    const deleteScheduled = async (task) => {
        if (!window.confirm('Delete this scheduled task?')) return;
        try {
            const resp = await axiosInstance.delete(`/api/agent-space/scheduled-tasks/${task.id}`);
            if (resp.data?.success) setScheduled((prev) => prev.filter((t) => t.id !== task.id));
        } catch (e) { console.error(e); }
    };

    // ----- Files filter -----
    // Count files per format so we know which pill labels to show (we hide
    // the ones with zero matches to keep the bar tight on small datasets).
    const filesCounts = useMemo(() => {
        const counts = { all: agentFiles.length, excel: 0, csv: 0, json: 0, pdf: 0, txt: 0, other: 0 };
        for (const f of agentFiles) {
            const t = (f.file_type || 'other').toLowerCase();
            if (t in counts && t !== 'all') counts[t] += 1;
            else if (t === 'docx' || t === 'doc') counts.other += 1;
            else if (!(t in counts)) counts.other += 1;
        }
        return counts;
    }, [agentFiles]);

    const filteredFiles = useMemo(() => {
        let rows = agentFiles;
        if (filesFilter !== 'all') {
            rows = rows.filter((f) => {
                const t = (f.file_type || 'other').toLowerCase();
                if (filesFilter === 'other') return !['excel', 'csv', 'json', 'pdf', 'txt'].includes(t);
                return t === filesFilter;
            });
        }
        if (filesSearch.trim()) {
            const q = filesSearch.trim().toLowerCase();
            rows = rows.filter((f) => (
                (f.filename || '').toLowerCase().includes(q)
                || (f.task_name || '').toLowerCase().includes(q)
                || (f.label || '').toLowerCase().includes(q)
                || (agentMap[f.agent_type]?.label || '').toLowerCase().includes(q)
            ));
        }
        return rows;
    }, [agentFiles, filesFilter, filesSearch, agentMap]);

    // ----- Scheduled filter -----
    const scheduledCounts = useMemo(() => ({
        all: scheduled.length,
        active: scheduled.filter((t) => t.status === 'active').length,
        paused: scheduled.filter((t) => t.status === 'paused').length,
    }), [scheduled]);

    const filteredScheduled = useMemo(() => {
        let rows = scheduled;
        if (scheduledFilter !== 'all') {
            rows = rows.filter((t) => t.status === scheduledFilter);
        }
        if (scheduledSearch.trim()) {
            const q = scheduledSearch.trim().toLowerCase();
            rows = rows.filter((t) => (
                (t.name || '').toLowerCase().includes(q)
                || (t.task_message || '').toLowerCase().includes(q)
                || (agentMap[t.agent_type]?.label || '').toLowerCase().includes(q)
            ));
        }
        return rows;
    }, [scheduled, scheduledFilter, scheduledSearch, agentMap]);

    // ---- Card derivation ----------------------------------------------
    const cards = useMemo(() => agentTypes.map((a) => {
        const connKey = a.connection_type || a.id;
        const conn = connectionStatuses[connKey];
        // General agent isn't OAuth-connected — it's configured once a data
        // source exists; connection agents need a live connection; the rest
        // are always ready.
        const isConfigured = a.id === 'general'
            ? generalConfigured
            : (a.requires_connection ? !!conn?.is_connected : true);
        const metrics = agentMetrics[a.id] || {};
        return { ...a, isConfigured, connectedEmail: conn?.connected_email || null, metrics };
    }), [agentTypes, connectionStatuses, agentMetrics, generalConfigured]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', fontFamily, gap: 1.5 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 1,
                px: { xs: 2, sm: 2.5 }, py: 1.5,
                bgcolor: 'rgba(255,255,255,0.7)',
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '14px',
                flexShrink: 0,
            }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: colors.textPrimary }}>
                        Agents
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary, mt: 0.25 }}>
                        Select an agent to view its activity, schedule tasks, or start a conversation.
                    </Typography>
                </Box>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {/* Show / hide the floating chatbot bubble. Persisted to
                        localStorage so the choice survives reloads. */}
                    <Tooltip title={fabVisible ? 'Hide chatbot button' : 'Show chatbot button'}>
                        <Box
                            onClick={toggleFabVisible}
                            sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                px: { xs: 1, sm: 1.25 }, py: 0.5,
                                borderRadius: '10px',
                                cursor: 'pointer',
                                bgcolor: fabVisible ? `${colors.accent}14` : 'rgba(0,0,0,0.04)',
                                color: fabVisible ? colors.accent : colors.textTertiary,
                                border: `1px solid ${fabVisible ? `${colors.accent}26` : 'rgba(0,0,0,0.06)'}`,
                                transition: 'all 0.18s ease',
                                '&:hover': {
                                    bgcolor: fabVisible ? `${colors.accent}1f` : 'rgba(0,0,0,0.06)',
                                },
                            }}
                        >
                            {fabVisible
                                ? <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                                : <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />}
                            <Typography sx={{
                                fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                display: { xs: 'none', sm: 'inline' },
                            }}>
                                {fabVisible ? 'Hide chat' : 'Show chat'}
                            </Typography>
                        </Box>
                    </Tooltip>

                    <Box sx={{
                        display: 'inline-flex',
                        bgcolor: 'rgba(0,0,0,0.04)',
                        borderRadius: '10px', p: '3px',
                        flexShrink: 0,
                    }}>
                        {[
                            { id: 'cards', label: 'Grid', icon: <GridViewOutlinedIcon sx={{ fontSize: 16 }} /> },
                            { id: 'list', label: 'List', icon: <ViewListOutlinedIcon sx={{ fontSize: 16 }} /> },
                        ].map((opt) => {
                            const active = viewMode === opt.id;
                            return (
                                <Tooltip key={opt.id} title={`${opt.label} view`}>
                                    <Box
                                        onClick={() => setViewMode(opt.id)}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                            px: { xs: 1, sm: 1.25 }, py: 0.5,
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            bgcolor: active ? '#fff' : 'transparent',
                                            color: active ? colors.accent : colors.textTertiary,
                                            boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                            transition: 'all 0.18s ease',
                                            '&:hover': { color: active ? colors.accent : colors.textSecondary },
                                        }}
                                    >
                                        {opt.icon}
                                        <Typography sx={{
                                            fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                            display: { xs: 'none', sm: 'inline' },
                                        }}>
                                            {opt.label}
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            {/* Scrollable body */}
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, pr: 0.5 }}>
                {/* Agent cards / list */}
                <Box sx={{
                    display: viewMode === 'cards' ? 'grid' : 'flex',
                    flexDirection: viewMode === 'list' ? 'column' : undefined,
                    gridTemplateColumns: viewMode === 'cards'
                        ? { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }
                        : undefined,
                    gap: viewMode === 'cards' ? 1.25 : 0.75,
                }}>
                    {agentTypesLoading
                        ? [1, 2, 3].map((i) => (
                            <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: '16px' }} />
                        ))
                        : cards.map((agent) => {
                            const agentColor = agent.color || colors.accent;
                            const metric = agent.metrics || {};
                            const statusText = agent.requires_connection
                                ? (agent.isConfigured ? 'Connected' : 'Not configured')
                                : (agent.id === 'general'
                                    ? (agent.isConfigured ? 'Active' : 'Not configured')
                                    : 'Active');
                            const statusColor = agent.isConfigured ? colors.success : '#f59e0b';
                            const primaryNumber = metric.total_conversations || 0;
                            let primaryLabel = 'Conversations';
                            if (agent.id === 'crm') primaryLabel = 'CRM interactions';
                            else if (agent.id === 'chat_history') primaryLabel = 'Chat sessions analyzed';
                            else if (agent.id === 'survey') primaryLabel = 'Survey analyses';
                            else if (agent.id === 'events') primaryLabel = 'Event tasks';
                            else if (agent.id === 'gmail' || agent.id === 'outlook') primaryLabel = 'Email tasks';
                            else if (agent.id === 'gmail-calendar') primaryLabel = 'Calendar tasks';
                            else if (agent.id === 'gmail_cleanup') primaryLabel = 'Cleanups run';

                            if (viewMode === 'list') {
                                return (
                                    <Box
                                        key={agent.id}
                                        onClick={() => onSelectAgent && onSelectAgent(agent.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 },
                                            px: { xs: 1.25, sm: 1.75 }, py: { xs: 1, sm: 1.25 },
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            border: '1px solid rgba(0,0,0,0.06)',
                                            bgcolor: 'rgba(255,255,255,0.7)',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                borderColor: `${agentColor}40`,
                                                bgcolor: 'rgba(255,255,255,0.95)',
                                                boxShadow: `0 2px 12px ${agentColor}14`,
                                                '& .row-chevron': { color: agentColor, transform: 'translateX(2px)' },
                                            },
                                        }}
                                    >
                                        <Box sx={{
                                            width: 36, height: 36, borderRadius: '10px',
                                            bgcolor: `${agentColor}14`, color: agentColor,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {getIcon(agent.icon_name, 20)}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.85rem', fontWeight: 600,
                                                color: colors.textPrimary, lineHeight: 1.3,
                                            }} noWrap>
                                                {agent.label}
                                            </Typography>
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.7rem', color: colors.textTertiary,
                                                mt: 0.25, lineHeight: 1.3,
                                            }} noWrap>
                                                {agent.description || agent.capability_description || ''}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-end', minWidth: 80, flexShrink: 0 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1 }}>
                                                {shortNumber(primaryNumber)}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary, mt: 0.25 }}>
                                                {primaryLabel}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
                                            <Chip
                                                size="small"
                                                icon={agent.isConfigured
                                                    ? <CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: `${colors.success} !important` }} />
                                                    : <RadioButtonUncheckedIcon sx={{ fontSize: '14px !important', color: '#94a3b8 !important' }} />}
                                                label={statusText}
                                                sx={{
                                                    fontFamily, fontSize: '0.62rem', fontWeight: 600,
                                                    height: 22,
                                                    color: agent.isConfigured ? colors.success : colors.textTertiary,
                                                    bgcolor: agent.isConfigured ? 'rgba(5,150,105,0.08)' : 'rgba(0,0,0,0.04)',
                                                    border: `1px solid ${agent.isConfigured ? 'rgba(5,150,105,0.18)' : 'rgba(0,0,0,0.08)'}`,
                                                    '& .MuiChip-icon': { ml: '6px' },
                                                }}
                                            />
                                        </Box>
                                        <Tooltip title="Agent settings">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); configureAgent(agent.id); }}
                                                sx={{
                                                    color: colors.textTertiary,
                                                    '&:hover': { color: agentColor, bgcolor: `${agentColor}10` },
                                                }}
                                            >
                                                <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <ChevronRightIcon
                                            className="row-chevron"
                                            sx={{ fontSize: 18, color: colors.textTertiary, transition: 'all 0.15s ease', flexShrink: 0 }}
                                        />
                                    </Box>
                                );
                            }

                            // Grid card
                            return (
                                <Box
                                    key={agent.id}
                                    onClick={() => onSelectAgent && onSelectAgent(agent.id)}
                                    sx={{
                                        position: 'relative',
                                        p: { xs: 1.5, sm: 2 },
                                        borderRadius: '16px',
                                        bgcolor: 'rgba(255,255,255,0.85)',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                                        cursor: 'pointer',
                                        transition: 'all 0.18s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            borderColor: `${agentColor}40`,
                                            boxShadow: `0 8px 22px ${agentColor}1c`,
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                                        <Box sx={{
                                            width: 38, height: 38, borderRadius: '12px',
                                            bgcolor: `${agentColor}14`, color: agentColor,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {getIcon(agent.icon_name, 22)}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary }} noWrap>
                                                {agent.label}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.68rem', color: colors.textTertiary, mt: 0.1 }} noWrap>
                                                Status: <Box component="span" sx={{ color: statusColor, fontWeight: 600 }}>{statusText}</Box>
                                                {metric.success_rate ? (
                                                    <Box component="span" sx={{ color: colors.textTertiary }}> · {metric.success_rate}% success</Box>
                                                ) : null}
                                            </Typography>
                                        </Box>
                                        <Tooltip title="Agent settings">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); configureAgent(agent.id); }}
                                                sx={{
                                                    color: colors.textTertiary,
                                                    '&:hover': { color: agentColor, bgcolor: `${agentColor}10` },
                                                }}
                                            >
                                                <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 0.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '1.7rem', fontWeight: 800, color: colors.textPrimary, lineHeight: 1 }}>
                                            {shortNumber(primaryNumber)}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textSecondary }}>
                                            {primaryLabel}
                                        </Typography>
                                    </Box>

                                    <Typography sx={{
                                        fontFamily, fontSize: '0.65rem', color: colors.textTertiary,
                                        mt: 0.75, lineHeight: 1.4,
                                    }} noWrap>
                                        {agent.connectedEmail
                                            ? `Connected: ${agent.connectedEmail}`
                                            : (agent.description || agent.capability_description || '')}
                                    </Typography>
                                </Box>
                            );
                        })}
                </Box>

                {/* ===========================================================
                    ACTIVITY (cross-agent, info-panel style)
                   =========================================================== */}
                <Box sx={{
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    overflow: 'hidden',
                    // Default flex-shrink (1) lets the card collapse below its
                    // natural content height inside the column-flex scrollable
                    // body, which makes the tabs row and the timeline body
                    // disappear when sibling sections are also present.
                    flexShrink: 0,
                }}>
                    {/* Section header — title, subtitle, tabs, search.
                        One clean toolbar that breathes more than the old design. */}
                    <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.01em' }}>
                                    Activity feed
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textTertiary, mt: 0.25 }}>
                                    {activityCounts.all} workflow{activityCounts.all !== 1 ? 's' : ''} across all agents
                                    {activityCounts.failed > 0 && <Box component="span" sx={{ color: colors.danger, fontWeight: 600 }}> · {activityCounts.failed} failed</Box>}
                                </Typography>
                            </Box>

                            {/* Search — custom pill control. Built with a native
                                <input> so the icon + field + clear button live
                                inside ONE border instead of MUI's adornment
                                rendering them as separate boxes. Focus halo uses
                                :focus-within on the wrapper. */}
                            {/* Search — custom pill control. */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.85,
                                width: { xs: '100%', sm: 340 },
                                height: 40,
                                px: 1.4,
                                bgcolor: '#fff',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: '12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                transition: 'all 0.18s ease',
                                flexShrink: 0,
                                '&:hover': { borderColor: 'rgba(0,0,0,0.18)' },
                                '&:focus-within': {
                                    borderColor: colors.accent,
                                    boxShadow: `0 0 0 3px ${colors.accent}1f`,
                                },
                            }}>
                                <SearchIcon sx={{
                                    fontSize: 17,
                                    color: search ? colors.accent : colors.textTertiary,
                                    transition: 'color 0.15s ease', flexShrink: 0,
                                }} />
                                <InputBase
                                    placeholder="Search by task, agent, step…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontFamily,
                                        fontSize: '0.8rem',
                                        color: colors.textPrimary,
                                        // Deep targets the inner HTML input to guarantee no native border resets conflict
                                        '& .MuiInputBase-input': {
                                            padding: 0,
                                            border: 0,
                                            outline: 'none',
                                            background: 'transparent',
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: colors.textTertiary,
                                            opacity: 1,
                                        },
                                    }}
                                />
                                {search && (
                                    <Tooltip title="Clear">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearch('')}
                                            sx={{
                                                p: 0.4, mr: -0.5,
                                                color: colors.textTertiary,
                                                '&:hover': { color: colors.danger, bgcolor: 'rgba(220,38,38,0.06)' },
                                            }}
                                        >
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Segmented status filter — pill style instead of underline tabs */}
                        <Box sx={{
                            display: 'inline-flex',
                            bgcolor: 'rgba(0,0,0,0.04)',
                            borderRadius: '10px',
                            p: '3px',
                        }}>
                            {[
                                { id: 'all', label: 'All', count: activityCounts.all },
                                { id: 'completed', label: 'Completed', count: activityCounts.completed },
                                { id: 'failed', label: 'Failed', count: activityCounts.failed },
                            ].map((tab) => {
                                const active = statusFilter === tab.id;
                                return (
                                    <Box
                                        key={tab.id}
                                        onClick={() => setStatusFilter(tab.id)}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                            cursor: 'pointer',
                                            px: 1.25, py: 0.6,
                                            borderRadius: '8px',
                                            bgcolor: active ? '#fff' : 'transparent',
                                            boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                            transition: 'all 0.18s ease',
                                        }}
                                    >
                                        <Typography sx={{
                                            fontFamily, fontSize: '0.74rem', fontWeight: 600,
                                            color: active ? colors.textPrimary : colors.textTertiary,
                                        }}>
                                            {tab.label}
                                        </Typography>
                                        <Box sx={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            minWidth: 18, height: 16, borderRadius: '6px', px: 0.5,
                                            bgcolor: active ? colors.accent : 'rgba(0,0,0,0.08)',
                                            color: active ? '#fff' : colors.textTertiary,
                                            fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                        }}>
                                            {tab.count}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.05)' }} />

                    {/* Timeline body */}
                    <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: 1, pb: 1.5 }}>
                        {activityLoading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1 }}>
                                {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: '14px' }} />)}
                            </Box>
                        ) : filteredActivity.length === 0 ? (
                            <Box sx={{
                                py: 5, textAlign: 'center', mx: 1,
                                borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.02)',
                                border: '1px dashed rgba(0,0,0,0.08)',
                            }}>
                                <AutoAwesomeIcon sx={{ fontSize: 32, color: 'rgba(0,0,0,0.14)', mb: 0.75 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600, color: colors.textSecondary }}>
                                    {search ? 'No tasks match your search.' : statusFilter !== 'all' ? `No ${statusFilter} tasks yet.` : 'No activity yet.'}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mt: 0.5 }}>
                                    Send a task in the chat to see it appear here.
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                ref={activityScrollRef}
                                onScroll={handleActivityScroll}
                                sx={{
                                    display: 'flex', flexDirection: 'column', gap: 1.25,
                                    maxHeight: 560,
                                    overflowY: 'auto',
                                    p: 0.5,
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.12)', borderRadius: '4px' },
                                }}
                            >
                                {/* Top-of-list pager. Same UX as the info panel:
                                      - hasMore + idle → clickable pill button
                                      - loadingMore   → spinner + "Loading…"
                                      - !hasMore (after multi-page load) → "Beginning of history" */}
                                {activityHasMore ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.75 }}>
                                        <Box
                                            onClick={handleLoadOlderActivity}
                                            role="button"
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.65,
                                                px: 1.6, py: 0.65,
                                                borderRadius: '999px',
                                                bgcolor: '#fff',
                                                border: `1px solid ${colors.accent}40`,
                                                color: colors.accent,
                                                cursor: activityLoadingMore ? 'progress' : 'pointer',
                                                opacity: activityLoadingMore ? 0.7 : 1,
                                                pointerEvents: activityLoadingMore ? 'none' : 'auto',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                                transition: 'all 0.18s ease',
                                                '&:hover': {
                                                    bgcolor: `${colors.accent}0a`,
                                                    borderColor: `${colors.accent}66`,
                                                    boxShadow: `0 4px 12px ${colors.accent}24`,
                                                    transform: 'translateY(-1px)',
                                                },
                                            }}
                                        >
                                            {activityLoadingMore ? (
                                                <Box sx={{
                                                    width: 12, height: 12, borderRadius: '50%',
                                                    border: `1.5px solid ${colors.accent}40`,
                                                    borderTopColor: colors.accent,
                                                    animation: 'agentLoadSpin 0.8s linear infinite',
                                                    '@keyframes agentLoadSpin': {
                                                        from: { transform: 'rotate(0deg)' },
                                                        to: { transform: 'rotate(360deg)' },
                                                    },
                                                }} />
                                            ) : (
                                                <KeyboardArrowUpIcon sx={{ fontSize: 15 }} />
                                            )}
                                            <Typography sx={{ fontFamily, fontSize: '0.72rem', fontWeight: 700 }}>
                                                {activityLoadingMore ? 'Loading…' : 'Load previous activity'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    filteredActivity.length > ACTIVITY_PAGE_SIZE && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.4, py: 0.75 }}>
                                            <HistoryIcon sx={{ fontSize: 13, color: colors.textTertiary }} />
                                            <Typography sx={{ fontFamily, fontSize: '0.64rem', color: colors.textTertiary, fontWeight: 600 }}>
                                                Beginning of history
                                            </Typography>
                                        </Box>
                                    )
                                )}
                                {filteredActivity.map((task) => {
                                    const def = agentMap[task.agent_type];
                                    const agentColor = def?.color || colors.accent;
                                    const isCompleted = task.status === 'completed';
                                    const isFailed = task.status === 'failed';
                                    const sc = isCompleted ? colors.success : isFailed ? colors.danger : agentColor;
                                    const statusBg = isCompleted ? 'rgba(5,150,105,0.12)' : isFailed ? 'rgba(220,38,38,0.12)' : `${agentColor}20`;
                                    const statusLabel = isCompleted ? 'Completed' : isFailed ? 'Failed' : 'In Progress';
                                    const isExpanded = expandedTasks.has(task.id);

                                    // Step kind → label/icon/color metadata. Used for both the
                                    // turn-based rendering and the legacy flat-step fallback.
                                    const kindMeta = {
                                        task_summary: { label: 'Task', icon: <BoltIcon sx={{ fontSize: 10 }} />, color: colors.accent },
                                        thought: { label: 'Thought', icon: <BoltIcon sx={{ fontSize: 10 }} />, color: colors.accent },
                                        tool_call: { label: 'Action', icon: <CheckCircleIcon sx={{ fontSize: 11 }} />, color: colors.success },
                                        error: { label: 'Error', icon: <ErrorIcon sx={{ fontSize: 11 }} />, color: colors.danger },
                                        system_log: { label: 'System', icon: <BoltIcon sx={{ fontSize: 10 }} />, color: colors.textTertiary },
                                        final: { label: 'Outcome', icon: <CheckCircleIcon sx={{ fontSize: 11 }} />, color: colors.success },
                                    };

                                    // Single step bullet (timeline dot + label + summary + optional
                                    // results preview when expanded). Shared between turn-based
                                    // rendering and the legacy flat-step path.
                                    const renderStepBullet = (s, i, scopedExpanded) => {
                                        const kind = s.kind || 'tool_call';
                                        const stepDone = s.status === 'completed';
                                        const stepFailed = s.status === 'failed' || kind === 'error';
                                        const stepRunning = s.status === 'running';
                                        const stepColor = stepFailed
                                            ? colors.danger
                                            : stepDone
                                                ? (kindMeta[kind]?.color || colors.success)
                                                : colors.warning;
                                        const meta = kindMeta[kind] || kindMeta.tool_call;
                                        const primary = (kind === 'tool_call'
                                            ? (s.output_summary || s.label || s.output_preview || 'Action')
                                            : (s.label || s.output_summary || s.output_preview || meta.label));
                                        const stepIcon = stepFailed
                                            ? <ErrorIcon sx={{ fontSize: 11 }} />
                                            : stepDone
                                                ? (meta.icon || <CheckCircleIcon sx={{ fontSize: 11 }} />)
                                                : <BoltIcon sx={{ fontSize: 10 }} />;
                                        return (
                                            <Box
                                                key={s.step_id || i}
                                                sx={{
                                                    display: 'flex', alignItems: 'flex-start', gap: 0.85,
                                                    // Subtle slide-in for newly-arrived steps during a live
                                                    // poll. React reuses DOM nodes for already-keyed steps,
                                                    // so existing rows don't re-animate.
                                                    animation: 'agentStepFadeIn 260ms ease-out both',
                                                    '@keyframes agentStepFadeIn': {
                                                        from: { opacity: 0, transform: 'translateY(4px)' },
                                                        to: { opacity: 1, transform: 'translateY(0)' },
                                                    },
                                                }}
                                            >
                                                <Box sx={{
                                                    mt: '3px', width: 18, height: 18, borderRadius: '50%',
                                                    bgcolor: `${stepColor}1a`, color: stepColor,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                    border: `2px solid #fff`,
                                                    boxShadow: `0 0 0 1px ${stepColor}28`,
                                                    // Pulse the dot while the step is still running so it's
                                                    // obvious the agent is mid-work.
                                                    ...(stepRunning ? {
                                                        animation: 'agentStepPulse 1.2s ease-in-out infinite',
                                                        '@keyframes agentStepPulse': {
                                                            '0%, 100%': { boxShadow: `0 0 0 0 ${stepColor}66, 0 0 0 1px ${stepColor}28` },
                                                            '50%': { boxShadow: `0 0 0 6px ${stepColor}00, 0 0 0 1px ${stepColor}28` },
                                                        },
                                                    } : {}),
                                                }}>
                                                    {stepIcon}
                                                </Box>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    {scopedExpanded && (
                                                        <Typography sx={{
                                                            fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                                            color: stepColor, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.2,
                                                        }}>
                                                            {meta.label}
                                                        </Typography>
                                                    )}
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.74rem',
                                                        color: stepFailed ? colors.danger : colors.textSecondary,
                                                        fontWeight: stepFailed ? 600 : 400,
                                                        lineHeight: 1.55,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: scopedExpanded ? 6 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                    }}>
                                                        {primary}
                                                    </Typography>
                                                    {stepFailed && s.hint && (
                                                        <Typography sx={{
                                                            fontFamily, fontSize: '0.66rem', color: colors.textTertiary,
                                                            lineHeight: 1.5, mt: 0.2, fontStyle: 'italic',
                                                        }}>
                                                            {s.hint}
                                                        </Typography>
                                                    )}
                                                    {stepRunning && (
                                                        <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.warning, mt: 0.15 }}>
                                                            Running…
                                                        </Typography>
                                                    )}
                                                    {scopedExpanded && kind === 'tool_call' && s.output_data && (
                                                        <Box sx={{ mt: 0.5 }}>
                                                            {renderStepResults(s.output_data)}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    };

                                    return (
                                        <Box
                                            key={task.id}
                                            onClick={() => task.agent_type && onSelectAgent && onSelectAgent(task.agent_type)}
                                            sx={{
                                                position: 'relative',
                                                p: { xs: 1.5, sm: 1.75 },
                                                borderRadius: '14px',
                                                bgcolor: '#fff',
                                                border: '1px solid rgba(0,0,0,0.07)',
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                                                cursor: task.agent_type ? 'pointer' : 'default',
                                                transition: 'all 0.18s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    borderColor: `${sc}40`,
                                                    boxShadow: `0 6px 18px ${sc}1a`,
                                                },
                                                // Accent strip on the left, the agent's color.
                                                '&::before': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    left: 0, top: 12, bottom: 12,
                                                    width: 3,
                                                    bgcolor: sc,
                                                    borderRadius: '0 3px 3px 0',
                                                },
                                            }}
                                        >
                                            {/* Top row: agent chip pill + status pill on right + timestamp far right */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                                <Box sx={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                                    px: 0.85, py: 0.3, borderRadius: '8px',
                                                    bgcolor: `${agentColor}12`,
                                                    border: `1px solid ${agentColor}26`,
                                                }}>
                                                    <Box sx={{ display: 'flex', color: agentColor }}>
                                                        {getIcon(def?.icon_name, 13)}
                                                    </Box>
                                                    <Typography sx={{ fontFamily, fontSize: '0.66rem', fontWeight: 700, color: agentColor }} noWrap>
                                                        {def?.label || task.agent_type}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.35,
                                                        px: 0.75, py: 0.2, borderRadius: '6px', bgcolor: statusBg,
                                                    }}>
                                                        {isCompleted && <CheckCircleIcon sx={{ fontSize: 11, color: sc }} />}
                                                        {isFailed && <ErrorIcon sx={{ fontSize: 11, color: sc }} />}
                                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', fontWeight: 700, color: sc }}>
                                                            {statusLabel}
                                                        </Typography>
                                                    </Box>
                                                    <Typography sx={{ fontFamily, fontSize: '0.66rem', color: colors.textTertiary, whiteSpace: 'nowrap' }}>
                                                        {formatDateTime(task.timestamp)}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Title — large, prominent */}
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.92rem', fontWeight: 700,
                                                color: colors.textPrimary, lineHeight: 1.35, mb: 0.5,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                            }}>
                                                {task.title}
                                            </Typography>

                                            {/* Body — turn-based session conversation (user prompts +
                                                agent replies with steps) when turns are available,
                                                otherwise fall back to flat step list for legacy entries. */}
                                            {(() => {
                                                if (task.turns && task.turns.length > 0) {
                                                    return (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, mt: 0.75 }}>
                                                            {task.turns.map((turn, idx) => {
                                                                if (turn.role === 'user') {
                                                                    return (
                                                                        <Box key={`u-${idx}`} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                            <Box sx={{
                                                                                maxWidth: '88%',
                                                                                bgcolor: `${agentColor}10`,
                                                                                border: `1px solid ${agentColor}28`,
                                                                                borderRadius: '12px',
                                                                                px: 1.25, py: 0.85,
                                                                            }}>
                                                                                <Typography sx={{
                                                                                    fontFamily, fontSize: '0.6rem', fontWeight: 700,
                                                                                    color: agentColor, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.2,
                                                                                }}>
                                                                                    You {turn.timestamp ? `· ${formatDateTime(turn.timestamp)}` : ''}
                                                                                </Typography>
                                                                                <Typography sx={{
                                                                                    fontFamily, fontSize: '0.78rem', color: colors.textPrimary,
                                                                                    lineHeight: 1.55, whiteSpace: 'pre-wrap',
                                                                                }}>
                                                                                    {turn.message || ''}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                }
                                                                // Agent turn: header label + step bullets + reply bubble.
                                                                const turnSteps = turn.step_details || [];
                                                                const defaultKinds = new Set(['tool_call', 'error', 'thought']);
                                                                const visibleTurnSteps = isExpanded
                                                                    ? turnSteps
                                                                    : turnSteps.filter((s) => defaultKinds.has(s.kind || 'tool_call'));
                                                                return (
                                                                    <Box key={`a-${idx}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                            <Box sx={{
                                                                                width: 16, height: 16, borderRadius: '5px',
                                                                                bgcolor: `${agentColor}14`, color: agentColor,
                                                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                                            }}>
                                                                                {getIcon(def?.icon_name, 10)}
                                                                            </Box>
                                                                            <Typography sx={{
                                                                                fontFamily, fontSize: '0.6rem', fontWeight: 700,
                                                                                color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px',
                                                                            }}>
                                                                                {def?.label || 'Agent'} {turn.timestamp ? `· ${formatDateTime(turn.timestamp)}` : ''}
                                                                            </Typography>
                                                                        </Box>
                                                                        {visibleTurnSteps.length > 0 && (
                                                                            <Box sx={{
                                                                                display: 'flex', flexDirection: 'column', gap: 0.65,
                                                                                pl: 1.25, ml: 0.5,
                                                                                borderLeft: visibleTurnSteps.length > 1 ? '2px solid rgba(0,0,0,0.05)' : 'none',
                                                                            }}>
                                                                                {visibleTurnSteps.map((s, i) => renderStepBullet(s, i, isExpanded))}
                                                                            </Box>
                                                                        )}
                                                                        {turn.message && (
                                                                            <Typography sx={{
                                                                                fontFamily, fontSize: '0.78rem', color: colors.textPrimary,
                                                                                lineHeight: 1.55, mt: 0.25, whiteSpace: 'pre-wrap',
                                                                                bgcolor: 'rgba(0,0,0,0.025)',
                                                                                border: '1px solid rgba(0,0,0,0.05)',
                                                                                borderRadius: '12px',
                                                                                px: 1.25, py: 0.85,
                                                                            }}>
                                                                                {turn.message}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                );
                                                            })}
                                                            {/* Single expand toggle for the whole session. */}
                                                            <Box
                                                                onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                                                                sx={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                                    mt: 0.25, cursor: 'pointer',
                                                                    color: colors.accent, width: 'fit-content',
                                                                    '&:hover': { opacity: 0.8 },
                                                                }}
                                                            >
                                                                <Typography sx={{ fontFamily, fontSize: '0.68rem', fontWeight: 700 }}>
                                                                    {isExpanded ? '− Show less' : '+ Show all step details'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                }

                                                // Legacy fallback: flat step list.
                                                const allSteps = task.steps || [];
                                                const defaultKinds = new Set(['tool_call', 'error', 'thought']);
                                                const visibleSteps = isExpanded
                                                    ? allSteps
                                                    : allSteps.filter((s) => defaultKinds.has(s.kind || 'tool_call'));
                                                const hiddenCount = (task.stepCount || allSteps.length) - visibleSteps.length;

                                                if (visibleSteps.length === 0 && hiddenCount === 0) {
                                                    return task.description && (
                                                        <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textSecondary, lineHeight: 1.55, mt: 0.5 }}>
                                                            {task.description}
                                                        </Typography>
                                                    );
                                                }
                                                return (
                                                    <Box sx={{
                                                        display: 'flex', flexDirection: 'column', gap: 0.75,
                                                        mt: 0.75, pl: 0.75,
                                                        borderLeft: visibleSteps.length > 1 ? '2px solid rgba(0,0,0,0.05)' : 'none',
                                                    }}>
                                                        {visibleSteps.map((s, i) => (
                                                            <Box key={s.step_id || i} sx={{ ml: -1.5 }}>
                                                                {renderStepBullet(s, i, isExpanded)}
                                                            </Box>
                                                        ))}
                                                        {(hiddenCount > 0 || isExpanded) && (
                                                            <Box
                                                                onClick={(e) => { e.stopPropagation(); toggleExpand(task.id); }}
                                                                sx={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                                    mt: 0.25, ml: 0.25, cursor: 'pointer',
                                                                    color: colors.accent, width: 'fit-content',
                                                                    '&:hover': { opacity: 0.8 },
                                                                }}
                                                            >
                                                                <Typography sx={{ fontFamily, fontSize: '0.68rem', fontWeight: 700 }}>
                                                                    {isExpanded ? '− Show less' : `+ ${hiddenCount} more step${hiddenCount !== 1 ? 's' : ''}`}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })()}

                                            {/* Attachments row — surfaces files spilled
                                                to GCS during this session (large tool
                                                output, generated reports). Renders below
                                                the turn-based conversation. */}
                                            {task.files && task.files.length > 0 && (
                                                <Box sx={{
                                                    mt: 1.25, pt: 1,
                                                    borderTop: '1px dashed rgba(0,0,0,0.08)',
                                                    display: 'flex', flexDirection: 'column', gap: 0.5,
                                                }}>
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                                        color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px',
                                                    }}>
                                                        Attachments · {task.files.length}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                                                        {task.files.map((f) => (
                                                            <Box
                                                                key={f.id}
                                                                component="a"
                                                                href={f.file_url || '#'}
                                                                target="_blank"
                                                                rel="noopener"
                                                                onClick={(e) => e.stopPropagation()}
                                                                sx={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                                                    px: 0.85, py: 0.3, borderRadius: '8px',
                                                                    textDecoration: 'none',
                                                                    bgcolor: `${agentColor}10`,
                                                                    border: `1px solid ${agentColor}24`,
                                                                    color: agentColor,
                                                                    '&:hover': { bgcolor: `${agentColor}18`, borderColor: `${agentColor}44` },
                                                                }}
                                                            >
                                                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 13 }} />
                                                                <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600 }}>
                                                                    {f.filename || f.label || 'File'}
                                                                </Typography>
                                                                {f.file_size != null && (
                                                                    <Typography sx={{ fontFamily, fontSize: '0.58rem', color: colors.textTertiary }}>
                                                                        · {formatBytes(f.file_size)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ===========================================================
                    AGENT-GENERATED FILES (cross-agent)
                    Same card style as the Activity feed so the page reads
                    as a single, consistent dashboard.
                   =========================================================== */}
                <Box sx={{
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    overflow: 'hidden',
                    flexShrink: 0,
                }}>
                    {/* Section header — title + search on row 1, segmented
                        format filter on row 2 (matches Activity feed). */}
                    <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.5 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                            gap: 1.5, flexWrap: 'wrap', mb: 1.5,
                        }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
                                    <FolderOpenOutlinedIcon sx={{ fontSize: 18, color: colors.accent }} />
                                    <Typography sx={{
                                        fontFamily, fontSize: '1rem', fontWeight: 700,
                                        color: colors.textPrimary, letterSpacing: '-0.01em',
                                    }}>
                                        Files
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textTertiary, mt: 0.25 }}>
                                    {agentFiles.length} file{agentFiles.length !== 1 ? 's' : ''} generated across all agents
                                </Typography>
                            </Box>

                            {/* Search — identical pill control to the Activity
                                feed (same width, same InputBase, same focus halo). */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.85,
                                width: { xs: '100%', sm: 340 },
                                height: 40,
                                px: 1.4,
                                bgcolor: '#fff',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: '12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                transition: 'all 0.18s ease',
                                flexShrink: 0,
                                '&:hover': { borderColor: 'rgba(0,0,0,0.18)' },
                                '&:focus-within': {
                                    borderColor: colors.accent,
                                    boxShadow: `0 0 0 3px ${colors.accent}1f`,
                                },
                            }}>
                                <SearchIcon sx={{
                                    fontSize: 17,
                                    color: filesSearch ? colors.accent : colors.textTertiary,
                                    transition: 'color 0.15s ease', flexShrink: 0,
                                }} />
                                <InputBase
                                    placeholder="Search files, tasks, agents…"
                                    value={filesSearch}
                                    onChange={(e) => setFilesSearch(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontFamily,
                                        fontSize: '0.8rem',
                                        color: colors.textPrimary,
                                        '& .MuiInputBase-input': {
                                            padding: 0,
                                            border: 0,
                                            outline: 'none',
                                            background: 'transparent',
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: colors.textTertiary,
                                            opacity: 1,
                                        },
                                    }}
                                />
                                {filesSearch && (
                                    <Tooltip title="Clear">
                                        <IconButton
                                            size="small"
                                            onClick={() => setFilesSearch('')}
                                            sx={{
                                                p: 0.4, mr: -0.5, color: colors.textTertiary,
                                                '&:hover': { color: colors.danger, bgcolor: 'rgba(220,38,38,0.06)' },
                                            }}
                                        >
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Segmented format filter — only show pills that have
                            files. "All" is always there as the reset. */}
                        <Box sx={{
                            display: 'inline-flex',
                            bgcolor: 'rgba(0,0,0,0.04)',
                            borderRadius: '10px',
                            p: '3px',
                            flexWrap: 'wrap',
                            gap: '2px',
                        }}>
                            {[
                                { id: 'all', label: 'All', count: filesCounts.all },
                                { id: 'excel', label: 'Excel', count: filesCounts.excel },
                                { id: 'csv', label: 'CSV', count: filesCounts.csv },
                                { id: 'json', label: 'JSON', count: filesCounts.json },
                                { id: 'pdf', label: 'PDF', count: filesCounts.pdf },
                                { id: 'txt', label: 'Text', count: filesCounts.txt },
                                { id: 'other', label: 'Other', count: filesCounts.other },
                            ]
                                .filter((t) => t.id === 'all' || t.count > 0)
                                .map((tab) => {
                                    const active = filesFilter === tab.id;
                                    return (
                                        <Box
                                            key={tab.id}
                                            onClick={() => setFilesFilter(tab.id)}
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                                cursor: 'pointer',
                                                px: 1.25, py: 0.6, borderRadius: '8px',
                                                bgcolor: active ? '#fff' : 'transparent',
                                                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                                transition: 'all 0.18s ease',
                                            }}
                                        >
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.74rem', fontWeight: 600,
                                                color: active ? colors.textPrimary : colors.textTertiary,
                                            }}>
                                                {tab.label}
                                            </Typography>
                                            <Box sx={{
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                minWidth: 18, height: 16, borderRadius: '6px', px: 0.5,
                                                bgcolor: active ? colors.accent : 'rgba(0,0,0,0.08)',
                                                color: active ? '#fff' : colors.textTertiary,
                                                fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                            }}>
                                                {tab.count}
                                            </Box>
                                        </Box>
                                    );
                                })}
                        </Box>
                    </Box>

                    {/* List */}
                    <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: 1, pb: 1.5 }}>
                        {agentFilesLoading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85, p: 0.5 }}>
                                {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: '12px' }} />)}
                            </Box>
                        ) : filteredFiles.length === 0 ? (
                            <Box sx={{
                                py: 4.5, textAlign: 'center', mx: 1,
                                borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.02)',
                                border: '1px dashed rgba(0,0,0,0.08)',
                            }}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: '12px',
                                    bgcolor: `${colors.accent}10`, color: colors.accent,
                                    mx: 'auto', mb: 1,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary }}>
                                    {agentFiles.length === 0
                                        ? 'No files yet'
                                        : filesSearch
                                            ? 'No files match your search'
                                            : `No ${filesFilter} files`}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mt: 0.4, maxWidth: 380, mx: 'auto' }}>
                                    {agentFiles.length === 0
                                        ? 'Files appear here when an agent produces output too large to inline — like exported spreadsheets, full reports, or document extracts.'
                                        : 'Try a different filter or search term.'}
                                </Typography>
                            </Box>
                        ) : (
                            <Box sx={{
                                display: 'flex', flexDirection: 'column', gap: 0.75,
                                maxHeight: 360, overflowY: 'auto',
                                p: 0.5,
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.12)', borderRadius: '4px' },
                            }}>
                                {filteredFiles.map((f) => {
                                    const def = agentMap[f.agent_type];
                                    const fcolor = def?.color || colors.accent;
                                    return (
                                        <Box
                                            key={f.id}
                                            component="a"
                                            href={f.file_url || '#'}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.25,
                                                px: 1.4, py: 1,
                                                borderRadius: '12px',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                bgcolor: '#fff',
                                                textDecoration: 'none',
                                                color: 'inherit',
                                                position: 'relative',
                                                transition: 'all 0.18s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    borderColor: `${fcolor}40`,
                                                    boxShadow: `0 6px 18px ${fcolor}1a`,
                                                    '& .file-open-icon': { color: fcolor },
                                                },
                                                '&::before': {
                                                    content: '""', position: 'absolute', left: 0, top: 10, bottom: 10,
                                                    width: 3, borderRadius: '0 3px 3px 0', bgcolor: fcolor,
                                                },
                                            }}
                                        >
                                            <Box sx={{
                                                width: 36, height: 36, borderRadius: '10px',
                                                bgcolor: `${fcolor}14`, color: fcolor,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                ml: 0.5,
                                            }}>
                                                <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                {/* Line 1: filename + agent CHIP (chip, not tiny icon —
                                                    so the agent is unmistakable). */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, flexWrap: 'wrap' }}>
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.82rem', fontWeight: 700,
                                                        color: colors.textPrimary,
                                                        flex: '0 1 auto', minWidth: 0,
                                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                    }}>
                                                        {f.filename || f.label || 'File'}
                                                    </Typography>
                                                    <Box sx={{
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                                        px: 0.85, py: 0.2, borderRadius: '6px',
                                                        bgcolor: `${fcolor}12`,
                                                        border: `1px solid ${fcolor}26`,
                                                        flexShrink: 0,
                                                    }}>
                                                        <Box sx={{ display: 'flex', color: fcolor }}>
                                                            {getIcon(def?.icon_name, 11)}
                                                        </Box>
                                                        <Typography sx={{
                                                            fontFamily, fontSize: '0.62rem', fontWeight: 700,
                                                            color: fcolor, whiteSpace: 'nowrap',
                                                        }}>
                                                            {def?.label || f.agent_type || 'Agent'}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Line 2: task name (with a "Task" prefix so users
                                                    don't confuse it with the filename). Backend now
                                                    falls back to the first user message when the
                                                    conversation has no title set. */}
                                                {(f.task_name || f.label) && (
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.7rem', fontWeight: 500,
                                                        color: colors.textSecondary, mt: 0.3,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                                    }}>
                                                        <Box component="span" sx={{
                                                            fontWeight: 700, color: colors.textTertiary,
                                                            textTransform: 'uppercase', letterSpacing: '0.4px',
                                                            fontSize: '0.56rem', mr: 0.5,
                                                        }}>
                                                            Task ·
                                                        </Box>
                                                        {f.task_name || f.label}
                                                    </Typography>
                                                )}

                                                {/* Line 3: format / size / timestamp */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.3, flexWrap: 'wrap' }}>
                                                    {f.file_type && (
                                                        <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary, fontWeight: 600 }}>
                                                            {f.file_type.toUpperCase()}
                                                        </Typography>
                                                    )}
                                                    {f.file_size != null && (
                                                        <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary }}>
                                                            · {formatBytes(f.file_size)}
                                                        </Typography>
                                                    )}
                                                    <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary }}>
                                                        · {formatDateTime(f.created_at)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <OpenInNewIcon
                                                className="file-open-icon"
                                                sx={{
                                                    fontSize: 16, color: colors.textTertiary,
                                                    transition: 'color 0.15s ease', flexShrink: 0,
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* ===========================================================
                    SCHEDULED TASKS (cross-agent)
                    Card-style list, consistent with Activity + Files.
                   =========================================================== */}
                <Box sx={{
                    bgcolor: '#fff',
                    border: '1px solid rgba(0,0,0,0.06)',
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                    overflow: 'hidden',
                    flexShrink: 0,
                }}>
                    {/* Section header — title + search row, then status pill row. */}
                    <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 2, pb: 1.5 }}>
                        <Box sx={{
                            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                            gap: 1.5, flexWrap: 'wrap', mb: 1.5,
                        }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
                                    <EventRepeatIcon sx={{ fontSize: 18, color: colors.accent }} />
                                    <Typography sx={{
                                        fontFamily, fontSize: '1rem', fontWeight: 700,
                                        color: colors.textPrimary, letterSpacing: '-0.01em',
                                    }}>
                                        Scheduled tasks
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.74rem', color: colors.textTertiary, mt: 0.25 }}>
                                    {scheduled.length} task{scheduled.length !== 1 ? 's' : ''}
                                    {scheduledCounts.active > 0 && (
                                        <Box component="span" sx={{ color: colors.success, fontWeight: 600 }}>
                                            {' · '}{scheduledCounts.active} active
                                        </Box>
                                    )}
                                </Typography>
                            </Box>

                            {/* Search — identical pill control to the Activity
                                feed (same width, same InputBase, same focus halo). */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 0.85,
                                width: { xs: '100%', sm: 340 },
                                height: 40,
                                px: 1.4,
                                bgcolor: '#fff',
                                border: '1px solid rgba(0,0,0,0.1)',
                                borderRadius: '12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                transition: 'all 0.18s ease',
                                flexShrink: 0,
                                '&:hover': { borderColor: 'rgba(0,0,0,0.18)' },
                                '&:focus-within': {
                                    borderColor: colors.accent,
                                    boxShadow: `0 0 0 3px ${colors.accent}1f`,
                                },
                            }}>
                                <SearchIcon sx={{
                                    fontSize: 17,
                                    color: scheduledSearch ? colors.accent : colors.textTertiary,
                                    transition: 'color 0.15s ease', flexShrink: 0,
                                }} />
                                <InputBase
                                    placeholder="Search tasks, agents…"
                                    value={scheduledSearch}
                                    onChange={(e) => setScheduledSearch(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        minWidth: 0,
                                        fontFamily,
                                        fontSize: '0.8rem',
                                        color: colors.textPrimary,
                                        '& .MuiInputBase-input': {
                                            padding: 0,
                                            border: 0,
                                            outline: 'none',
                                            background: 'transparent',
                                        },
                                        '& .MuiInputBase-input::placeholder': {
                                            color: colors.textTertiary,
                                            opacity: 1,
                                        },
                                    }}
                                />
                                {scheduledSearch && (
                                    <Tooltip title="Clear">
                                        <IconButton
                                            size="small"
                                            onClick={() => setScheduledSearch('')}
                                            sx={{
                                                p: 0.4, mr: -0.5, color: colors.textTertiary,
                                                '&:hover': { color: colors.danger, bgcolor: 'rgba(220,38,38,0.06)' },
                                            }}
                                        >
                                            <ClearIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Segmented status filter */}
                        <Box sx={{
                            display: 'inline-flex',
                            bgcolor: 'rgba(0,0,0,0.04)',
                            borderRadius: '10px',
                            p: '3px',
                        }}>
                            {[
                                { id: 'all', label: 'All', count: scheduledCounts.all, accent: colors.accent },
                                { id: 'active', label: 'Active', count: scheduledCounts.active, accent: colors.success },
                                { id: 'paused', label: 'Paused', count: scheduledCounts.paused, accent: colors.warning },
                            ]
                                .filter((t) => t.id === 'all' || t.count > 0)
                                .map((tab) => {
                                    const active = scheduledFilter === tab.id;
                                    return (
                                        <Box
                                            key={tab.id}
                                            onClick={() => setScheduledFilter(tab.id)}
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.6,
                                                cursor: 'pointer',
                                                px: 1.25, py: 0.6, borderRadius: '8px',
                                                bgcolor: active ? '#fff' : 'transparent',
                                                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                                transition: 'all 0.18s ease',
                                            }}
                                        >
                                            <Typography sx={{
                                                fontFamily, fontSize: '0.74rem', fontWeight: 600,
                                                color: active ? colors.textPrimary : colors.textTertiary,
                                            }}>
                                                {tab.label}
                                            </Typography>
                                            <Box sx={{
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                minWidth: 18, height: 16, borderRadius: '6px', px: 0.5,
                                                bgcolor: active ? tab.accent : 'rgba(0,0,0,0.08)',
                                                color: active ? '#fff' : colors.textTertiary,
                                                fontFamily, fontSize: '0.58rem', fontWeight: 700,
                                            }}>
                                                {tab.count}
                                            </Box>
                                        </Box>
                                    );
                                })}
                        </Box>
                    </Box>

                    <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.05)' }} />

                    {/* Scrollable card list */}
                    <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: 1, pb: 1.5 }}>
                        {scheduledLoading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.85, p: 0.5 }}>
                                {[1, 2].map((i) => <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: '12px' }} />)}
                            </Box>
                        ) : filteredScheduled.length === 0 ? (
                            <Box sx={{
                                py: 4.5, textAlign: 'center', mx: 1,
                                borderRadius: '14px', bgcolor: 'rgba(0,0,0,0.02)',
                                border: '1px dashed rgba(0,0,0,0.08)',
                            }}>
                                <Box sx={{
                                    width: 44, height: 44, borderRadius: '12px',
                                    bgcolor: `${colors.accent}10`, color: colors.accent,
                                    mx: 'auto', mb: 1,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <EventRepeatIcon sx={{ fontSize: 22 }} />
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 700, color: colors.textPrimary }}>
                                    {scheduled.length === 0
                                        ? 'Nothing scheduled'
                                        : scheduledSearch
                                            ? 'No tasks match your search'
                                            : `No ${scheduledFilter} tasks`}
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.72rem', color: colors.textTertiary, mt: 0.4, maxWidth: 360, mx: 'auto' }}>
                                    {scheduled.length === 0
                                        ? 'Click the clock icon inside an agent chat to schedule a recurring task — daily, weekly, monthly, every N minutes / hours, or one-time.'
                                        : 'Try a different filter or search term.'}
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                ref={scheduledScrollRef}
                                onScroll={handleScheduledScroll}
                                sx={{
                                    display: 'flex', flexDirection: 'column', gap: 0.75,
                                    maxHeight: 380, overflowY: 'auto',
                                    p: 0.5,
                                    '&::-webkit-scrollbar': { width: '6px' },
                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.12)', borderRadius: '4px' },
                                }}
                            >
                                {scheduledVisible < filteredScheduled.length && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary }}>
                                            Scroll up for older… ({filteredScheduled.length - scheduledVisible} more)
                                        </Typography>
                                    </Box>
                                )}
                                {[...filteredScheduled].slice(0, scheduledVisible).reverse().map((t) => {
                                    const isActive = t.status === 'active';
                                    const isOnce = t.frequency === 'once';
                                    const def = agentMap[t.agent_type];
                                    const accent = def?.color || colors.accent;
                                    const typeColor = isOnce ? colors.warning : colors.accent;
                                    // Pretty cadence label, including "every N min/hr" detail.
                                    let cadence = t.frequency;
                                    if (t.frequency === 'minutely' && t.interval_value) {
                                        cadence = `Every ${t.interval_value} min`;
                                    } else if (t.frequency === 'hourly' && t.interval_value) {
                                        cadence = `Every ${t.interval_value}h`;
                                    } else if (isOnce) {
                                        cadence = 'One-time';
                                    } else if (cadence) {
                                        cadence = cadence.charAt(0).toUpperCase() + cadence.slice(1);
                                    }
                                    return (
                                        <Box
                                            key={t.id}
                                            onClick={() => t.agent_type && onSelectAgent && onSelectAgent(t.agent_type)}
                                            sx={{
                                                position: 'relative',
                                                display: 'flex', alignItems: 'center', gap: 1.25,
                                                px: 1.4, py: 1.1,
                                                borderRadius: '12px',
                                                border: '1px solid rgba(0,0,0,0.06)',
                                                bgcolor: '#fff',
                                                cursor: t.agent_type ? 'pointer' : 'default',
                                                transition: 'all 0.18s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-1px)',
                                                    borderColor: `${accent}40`,
                                                    boxShadow: `0 6px 18px ${accent}1a`,
                                                },
                                                '&::before': {
                                                    content: '""', position: 'absolute',
                                                    left: 0, top: 10, bottom: 10,
                                                    width: 3, borderRadius: '0 3px 3px 0',
                                                    bgcolor: isActive ? accent : 'rgba(0,0,0,0.15)',
                                                },
                                            }}
                                        >
                                            {/* Status indicator dot */}
                                            <Box sx={{
                                                width: 36, height: 36, borderRadius: '10px',
                                                bgcolor: `${accent}14`, color: accent,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                flexShrink: 0, ml: 0.5,
                                                position: 'relative',
                                                ...(isActive && !isOnce ? {
                                                    '&::after': {
                                                        content: '""', position: 'absolute',
                                                        top: 4, right: 4,
                                                        width: 7, height: 7, borderRadius: '50%',
                                                        bgcolor: colors.success,
                                                        border: '2px solid #fff',
                                                        animation: 'schedDotPulse 1.6s ease-in-out infinite',
                                                        '@keyframes schedDotPulse': {
                                                            '0%, 100%': { boxShadow: `0 0 0 0 ${colors.success}66` },
                                                            '50%': { boxShadow: `0 0 0 5px ${colors.success}00` },
                                                        },
                                                    },
                                                } : {}),
                                            }}>
                                                {getIcon(def?.icon_name, 18)}
                                            </Box>

                                            {/* Task body */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.3 }}>
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.78rem', fontWeight: 700,
                                                        color: colors.textPrimary, flex: 1, minWidth: 0,
                                                    }} noWrap>
                                                        {t.name || t.task_message || `Task #${t.id}`}
                                                    </Typography>
                                                    <Chip
                                                        size="small"
                                                        label={cadence}
                                                        sx={{
                                                            fontFamily, fontSize: '0.58rem', height: 18,
                                                            fontWeight: 700,
                                                            bgcolor: `${typeColor}14`, color: typeColor,
                                                            border: `1px solid ${typeColor}28`,
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, flexWrap: 'wrap' }}>
                                                    <AgentBadge agentType={t.agent_type || t.task_type} agentMap={agentMap} />
                                                    <Typography sx={{
                                                        fontFamily, fontSize: '0.62rem',
                                                        color: isActive ? colors.success : colors.textTertiary,
                                                        fontWeight: 600, textTransform: 'capitalize',
                                                        display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                                    }}>
                                                        <Box sx={{
                                                            width: 5, height: 5, borderRadius: '50%',
                                                            bgcolor: isActive ? colors.success : colors.textTertiary,
                                                        }} />
                                                        {t.status}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.62rem', color: colors.textTertiary }}>
                                                        · Next: {formatFuture(t.next_run_at)}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Action buttons */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title={isActive ? 'Pause' : 'Resume'}>
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleScheduled(t); }} sx={{ p: 0.5 }}>
                                                        {isActive
                                                            ? <PauseCircleOutlineIcon sx={{ fontSize: 18, color: colors.textTertiary }} />
                                                            : <PlayCircleOutlineIcon sx={{ fontSize: 18, color: colors.success }} />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteScheduled(t); }} sx={{ p: 0.5 }}>
                                                        <DeleteOutlineIcon sx={{ fontSize: 18, color: colors.danger }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
